import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PinInput from '../components/PinInput';
import { ApiService } from '../services/apiService';
import { AppHeader } from '../components/AppHeader';
import { parseDevState } from '../dev/devPreview';
import { DEV_PREVIEW_FINALISE_SELL } from '../dev/devPreviewMocks';
import './FinaliseSell.css';

// @mcc-dev-preview:start dev-preview-types
type FinaliseSellDevState =
  | 'validating'
  | 'pin-form'
  | 'transferring'
  | 'prepare-error'
  | 'validation-error'
  | 'order-expired';

const FINALISE_SELL_DEV_STATES: FinaliseSellDevState[] = [
  'validating',
  'pin-form',
  'transferring',
  'prepare-error',
  'validation-error',
  'order-expired',
];
// @mcc-dev-preview:end dev-preview-types

function isOrderExpiredError(message: string): boolean {
  return /expired/i.test(message);
}

const SELL_TIME_LIMIT_NOTICE =
  'You have about 1 hour to enter your PIN here to complete this transaction before it expires. If you miss that window, the order may expire and you will need to start a new sale.';

const FinaliseSell: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const devState = parseDevState(searchParams, FINALISE_SELL_DEV_STATES);

  const [cardId, setCardId] = useState<string | null>(null);
  /** Banxa order id from deeplink URL */
  const [banxaOrderId, setBanxaOrderId] = useState<string | null>(null);
  /** Internal UUID passed to transferCoin */
  const [internalOrderId, setInternalOrderId] = useState<string | null>(null);
  const [blockchain, setBlockchain] = useState<string | null>(null);
  const [coinType, setCoinType] = useState<string | null>(null);
  const [coinAmount, setCoinAmount] = useState<string | null>(null);

  const [pin, setPin] = useState('');
  const [verifyPin, setVerifyPin] = useState('');
  const [pinMethod, setPinMethod] = useState<any>(null);

  const [isValidating, setIsValidating] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [preparedCryptoAmount, setPreparedCryptoAmount] = useState<number | null>(null);
  const [preparedAt, setPreparedAt] = useState<string | null>(null);
  const [prepareNote, setPrepareNote] = useState<string | null>(null);

  const loadSigningMethods = useCallback(async (cid: string) => {
    const signingMethodsResponse = await ApiService.getUserSigningMethods(cid);
    if (!signingMethodsResponse.success || !signingMethodsResponse.data?.signingMethods) {
      throw new Error('Failed to get user signing methods');
    }
    const methods = signingMethodsResponse.data.signingMethods;
    const pinMethodFound = methods.find((method: any) => method.type === 'PIN');
    if (!pinMethodFound) {
      throw new Error('No PIN method found. Please activate your card first.');
    }
    setPinMethod(pinMethodFound);
  }, []);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    const mock = DEV_PREVIEW_FINALISE_SELL;
    setCardId(mock.cardId);
    setBanxaOrderId(mock.banxaOrderId);
    setInternalOrderId(mock.internalOrderId);
    setBlockchain(mock.blockchain);
    setCoinType(mock.coinType);
    setCoinAmount(mock.coinAmount);
    setPinMethod(mock.pinMethod);

    if (devState === 'validating') {
      setIsValidating(true);
      return;
    }

    setIsValidating(false);

    switch (devState) {
      case 'pin-form':
        setPreparedCryptoAmount(mock.preparedCryptoAmount);
        setPreparedAt(mock.preparedAt);
        setPrepareNote(mock.prepareNote);
        break;
      case 'transferring':
        setPreparedCryptoAmount(mock.preparedCryptoAmount);
        setIsLoading(true);
        break;
      case 'prepare-error':
        setPrepareError(
          searchParams.get('errorMessage') ??
            'Failed to prepare transfer. Please try again.'
        );
        break;
      case 'validation-error':
        setValidationError(
          searchParams.get('errorMessage') ??
            'Order validation failed. Please return from checkout to continue.'
        );
        break;
      case 'order-expired':
        setValidationError('This sell order has expired. Your crypto was not sent in time.');
        break;
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end dev-preview-seed

  // Banxa deeplink: validate order params, then load signing methods
  useEffect(() => {
    if (devState) return;

    const cardIdParam = searchParams.get('cardId');
    const orderIdParam = searchParams.get('orderId');
    const providerWalletAddressParam = searchParams.get('providerWalletAddress');
    const blockchainParam = searchParams.get('blockchain');
    const coinTypeParam = searchParams.get('coinType');
    const coinAmountParam = searchParams.get('coinAmount');
    const customerWalletAddressParam = searchParams.get('customerWalletAddress');

    if (
      !cardIdParam ||
      !orderIdParam ||
      !providerWalletAddressParam ||
      !blockchainParam ||
      !coinTypeParam ||
      !coinAmountParam
    ) {
      const missing: string[] = [];
      if (!cardIdParam) missing.push('cardId');
      if (!orderIdParam) missing.push('orderId');
      if (!providerWalletAddressParam) missing.push('providerWalletAddress');
      if (!blockchainParam) missing.push('blockchain');
      if (!coinTypeParam) missing.push('coinType');
      if (!coinAmountParam) missing.push('coinAmount');

      setValidationError(
        `Missing required return parameters: ${missing.join(', ')}. Please return from checkout to continue.`
      );
      setIsValidating(false);
      return;
    }

    setCardId(cardIdParam);
    setBanxaOrderId(orderIdParam);
    setBlockchain(blockchainParam);
    setCoinType(coinTypeParam);
    setCoinAmount(coinAmountParam);

    const run = async () => {
      let validatedOrderId: string | null = null;
      try {
        setIsValidating(true);
        setError(null);
        setValidationError(null);
        setPrepareError(null);

        const validationResponse = await ApiService.validateSellOrder({
          cardId: cardIdParam,
          orderId: orderIdParam,
          providerWalletAddress: providerWalletAddressParam,
          blockchain: blockchainParam,
          coinType: coinTypeParam,
          coinAmount: coinAmountParam,
          customerWalletAddress: customerWalletAddressParam || undefined
        });

        if (!validationResponse.success || !validationResponse.data?.orderId) {
          throw new Error(validationResponse.error || 'Order validation failed');
        }

        const internalId = validationResponse.data.orderId;
        validatedOrderId = internalId;
        setInternalOrderId(internalId);

        const prepareResponse = await ApiService.prepareSellTransfer({
          cardId: cardIdParam,
          orderId: internalId,
        });
        if (!prepareResponse.success || !prepareResponse.data) {
          throw new Error(prepareResponse.error || 'Failed to prepare transfer');
        }
        setPreparedCryptoAmount(prepareResponse.data.preparedCryptoAmount);
        setPreparedAt(prepareResponse.data.venlyPreparedAt);
        setPrepareNote(prepareResponse.data.estimationNote);

        await loadSigningMethods(cardIdParam);
      } catch (e: any) {
        console.error('❌ FinaliseSell:', e);
        const msg = e.message || 'Failed to validate order';
        if (validatedOrderId) {
          setPrepareError(msg);
          setValidationError(null);
        } else {
          setValidationError(msg);
        }
      } finally {
        setIsValidating(false);
      }
    };

    void run();
  }, [searchParams, loadSigningMethods, devState]);

  const handlePinChange = (value: string) => {
    setPin(value);
  };

  const handleVerifyPinChange = (value: string) => {
    setVerifyPin(value);
  };

  const handleSubmit = async () => {
    if (devState) return;

    if (pin.length !== 6 || verifyPin.length !== 6) {
      setError('Please enter a complete 6-digit PIN in both fields');
      return;
    }

    if (pin !== verifyPin) {
      setError('PINs do not match. Please try again.');
      return;
    }

    if (!pinMethod) {
      setError('PIN method not found. Please refresh the page.');
      return;
    }

    if (!cardId || !internalOrderId) {
      setError('Missing required information. Please refresh the page.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await ApiService.transferCoin({
        cardId,
        orderId: internalOrderId,
        pin,
        signingMethodId: pinMethod.id
      });

      if (!response.success) {
        throw new Error(response.error || 'Transfer failed');
      }

      const warn = (response as any).data?.banxaConfirmWarning as string | undefined;
      if (warn) {
        console.warn('Banxa confirm warning:', warn);
      }

      alert(
        warn
          ? 'Crypto transfer completed. Order confirmation had an issue — support may still reconcile your sale. Check your email.'
          : 'Crypto transfer completed successfully! Your sale is being processed.'
      );
      navigate(`/balance-display?cardId=${cardId}`);
    } catch (e: any) {
      console.error('❌ FinaliseSell: transfer error:', e);
      setError(e.message || 'Failed to transfer coin. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (devState) return;
    navigate(-1);
  };

  if (isValidating) {
    return (
      <div className="finalise-sell-container ev-page ev-page-enter">
        <AppHeader />
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', padding: '40px 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', textAlign: 'center' }}>
            <div className="spinner"></div>
            <p style={{ color: '#ffffff', fontSize: '16px', margin: 0, fontWeight: 500 }}>Validating order and preparing transfer...</p>
          </div>
        </main>
      </div>
    );
  }

  if (prepareError) {
    return (
      <div className="finalise-sell-container ev-page ev-page-enter">
        <AppHeader />
        <main className="finalise-sell-main">
          <div className="content-wrapper">
            <div className="error-container">
              <h2>Transfer Preparation Failed</h2>
              <p>{prepareError}</p>
              <button type="button" className="back-button" onClick={() => window.location.reload()} aria-label="Retry">
                <span>Retry</span>
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (validationError) {
    const orderExpired = isOrderExpiredError(validationError);
    return (
      <div className="finalise-sell-container ev-page ev-page-enter">
        <AppHeader />
        <main className="finalise-sell-main">
          <div className="content-wrapper">
            <div className="error-container">
              <h2>{orderExpired ? 'Order Expired' : 'Order Validation Failed'}</h2>
              <p>
                {orderExpired
                  ? 'This sell order has expired. Your crypto was not sent in time. You can start a new sale from your card.'
                  : validationError}
              </p>
              {orderExpired && cardId ? (
                <button
                  type="button"
                  className="submit-button"
                  onClick={() => navigate(`/card/sell?cardId=${encodeURIComponent(cardId)}`)}
                  aria-label="Start a new sale"
                >
                  Start a new sale
                </button>
              ) : (
                <button type="button" className="back-button" onClick={handleBack} aria-label="Go back">
                  <img src="/back-icon.svg" alt="" className="back-button-icon" aria-hidden="true" />
                  <span>Go Back</span>
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="finalise-sell-container ev-page ev-page-enter">
        <AppHeader />
        <main className="finalise-sell-main">
          <div className="content-wrapper">
            <div className="transfer-loading">
              <div className="transfer-icon">⏳</div>
              <h2 className="transfer-title">Transferring Crypto</h2>
              <p className="transfer-message">Please wait while we transfer your crypto...</p>

              <div className="progress-section">
                <div className="progress-bar">
                  <div className="progress-fill"></div>
                </div>
                <div className="progress-text">Processing transfer...</div>
              </div>

              <div className="transfer-details">
                <div className="detail-item">
                  <span className="detail-label">Amount:</span>
                  <span className="detail-value">
                    {coinAmount} {coinType}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Blockchain:</span>
                  <span className="detail-value">{blockchain}</span>
                </div>
              </div>

              <div className="loading-animation">
                <div className="spinner"></div>
                <p>Please do not close this page...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="finalise-sell-container ev-page ev-page-enter">
      <AppHeader />
      <main className="finalise-sell-main">
        <div className="content-wrapper">
          <h1 className="main-heading">FINALISE SELL</h1>
          <p className="main-subtitle">Enter your PIN to confirm and broadcast the transfer</p>

          <div className="sell-time-limit-notice" role="note">
            <p>{SELL_TIME_LIMIT_NOTICE}</p>
          </div>

          {preparedCryptoAmount != null && (
            <div className="prepared-transfer-summary" style={{ marginBottom: '1rem', fontSize: '14px' }}>
              <p>
                <strong>Prepared amount:</strong> {preparedCryptoAmount} {coinType}
              </p>
              {prepareNote && <p style={{ color: '#666' }}>{prepareNote}</p>}
              {preparedAt && (
                <p style={{ color: '#666', fontSize: '12px' }}>
                  Prepared at {new Date(preparedAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          <div className="finalise-form-container">
            <div className="finalise-sell-form">
              <div className="order-summary">
                <h3>Order Summary</h3>
                <div className="summary-item">
                  <span className="summary-label">Amount:</span>
                  <span className="summary-value">
                    {coinAmount} {coinType}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Blockchain:</span>
                  <span className="summary-value">{blockchain}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Order ID:</span>
                  <span className="summary-value">{internalOrderId || banxaOrderId}</span>
                </div>
              </div>

              <div className="pin-confirmation-text">
                <h3>Enter your PIN to confirm transfer</h3>
                <p className="pin-instruction">This will transfer your crypto to complete your sale</p>
              </div>

              <div className="pin-section">
                <PinInput
                  pin={pin}
                  verifyPin={verifyPin}
                  onPinChange={handlePinChange}
                  onVerifyPinChange={handleVerifyPinChange}
                  isLoading={isLoading}
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="finalise-sell-buttons">
                <button
                  type="button"
                  className="back-button"
                  onClick={handleBack}
                  disabled={isLoading}
                  aria-label="Go back"
                >
                  <img src="/back-icon.svg" alt="" className="back-button-icon" aria-hidden="true" />
                  <span>Back</span>
                </button>
                <button
                  className="submit-button"
                  onClick={handleSubmit}
                  disabled={pin.length !== 6 || verifyPin.length !== 6 || isLoading}
                >
                  {isLoading ? 'Transferring...' : 'Transfer Crypto'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FinaliseSell;
